'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className"];
import PropTypes from 'prop-types';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TableCellButtonGroup = styled(UIButtonGroup).withConfig({
  displayName: "TableCellFloatyPopoverBody__TableCellButtonGroup",
  componentId: "d6zuvw-0"
})(["& .cellFloatyButton{padding:2px 3px;}& span > span{margin-right:0;}"]);
const i18nKey = key => `RichTextLib.table.cellFloaty.${key}`;
const renderInsertDropdownButton = ({
  buttonClassName,
  dropdownClassName,
  cellsSpanRowsCount,
  cellsSpanColsCount,
  onInsertRowAbove,
  onInsertRowBelow,
  onInsertColumnLeft,
  onInsertColumnRight
}) => {
  return /*#__PURE__*/_jsx(UITooltip, {
    placement: "top",
    title: I18n.text(i18nKey('tooltip.insert')),
    children: /*#__PURE__*/_jsx(UIDropdown, {
      "data-test-id": "insert-cells-btn",
      placement: "bottom",
      arrowSize: "medium",
      iconName: "add",
      buttonClassName: buttonClassName,
      buttonUse: "tertiary-light",
      buttonSize: "xs",
      dropdownClassName: dropdownClassName,
      caretRenderer: () => null,
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "insert-row-above-btn",
          onClick: onInsertRowAbove,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('insert.rowAbove'),
            options: {
              count: cellsSpanRowsCount
            }
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "insert-row-below-btn",
          onClick: onInsertRowBelow,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('insert.rowBelow'),
            options: {
              count: cellsSpanRowsCount
            }
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "insert-col-left-btn",
          onClick: onInsertColumnLeft,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('insert.colLeft'),
            options: {
              count: cellsSpanColsCount
            }
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "insert-col-right-btn",
          onClick: onInsertColumnRight,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('insert.colRight'),
            options: {
              count: cellsSpanColsCount
            }
          })
        })]
      })
    })
  });
};
const renderDeleteDropdownButton = ({
  buttonClassName,
  dropdownClassName,
  cellsSpanRowsCount,
  cellsSpanColsCount,
  onDeleteRow,
  onDeleteColumn
}) => {
  return /*#__PURE__*/_jsx(UITooltip, {
    placement: "top",
    title: I18n.text(i18nKey('tooltip.delete')),
    children: /*#__PURE__*/_jsx(UIDropdown, {
      "data-test-id": "delete-cells-btn",
      placement: "bottom",
      arrowSize: "medium",
      iconName: "minus",
      buttonClassName: buttonClassName,
      buttonUse: "tertiary-light",
      buttonSize: "xs",
      dropdownClassName: dropdownClassName,
      caretRenderer: () => null,
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "delete-row-btn",
          onClick: onDeleteRow,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('delete.row'),
            options: {
              count: cellsSpanRowsCount
            }
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "delete-col-btn",
          onClick: onDeleteColumn,
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('delete.col'),
            options: {
              count: cellsSpanColsCount
            }
          })
        })]
      })
    })
  });
};
const renderMergeSplitButton = ({
  showMerge,
  showSplit,
  onMergeCells,
  onSplitCells,
  buttonClassName
}) => {
  if (!showMerge && !showSplit) return null;
  const preventMouseDown = evt => {
    // Prevent default to avoid blurring the editor in FF
    // see https://git.hubteam.com/HubSpot/rich-text-lib/issues/344
    evt.preventDefault();
  };
  const iconName = showMerge ? 'merge' : 'splitModule';
  const onClick = showMerge ? onMergeCells : onSplitCells;
  return /*#__PURE__*/_jsx(UITooltip, {
    placement: "top",
    title: I18n.text(i18nKey(`tooltip.${iconName}`)),
    children: /*#__PURE__*/_jsx(UIButton, {
      "data-test-id": "merge-split-btn",
      use: "tertiary-light",
      size: "xs",
      className: buttonClassName,
      onClick: onClick,
      onMouseDown: preventMouseDown,
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: iconName
      })
    })
  });
};
const propTypes = {
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  onMergeCells: PropTypes.func.isRequired,
  onSplitCells: PropTypes.func.isRequired,
  showMerge: PropTypes.bool,
  showSplit: PropTypes.bool
};
function TableFloatyPopoverBody(props) {
  const {
      className
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const buttonProps = Object.assign({
    buttonClassName: 'cellFloatyButton'
  }, rest);
  return /*#__PURE__*/_jsxs(TableCellButtonGroup, {
    className: className,
    children: [renderInsertDropdownButton(buttonProps), renderDeleteDropdownButton(buttonProps), renderMergeSplitButton(buttonProps)]
  });
}
TableFloatyPopoverBody.propTypes = propTypes;
export default TableFloatyPopoverBody;